import { useContractData } from '@/hooks/useContractData';
import { ClauseCategory } from '@/utils/clauseMapping';

/**
 * Agent contract clauses hook - now uses unified system
 * This ensures agents see the same contract data as buyers and sellers
 */
export const useContractClauses = (contractId: string) => {
  const { comprehensiveClauses } = useContractData(contractId);

  // Convert comprehensive clauses to the old format for backward compatibility
  const contractClauses: { title: string; description: string }[] = [];

  comprehensiveClauses.forEach((category) => {
    category.clauses.forEach((clause) => {
      contractClauses.push({
        title: clause.title,
        description: clause.description,
      });

      // Add sub-clauses if they exist
      if (clause.subClauses) {
        clause.subClauses.forEach((subClause) => {
          contractClauses.push({
            title: subClause.title,
            description: subClause.description,
          });
        });
      }
    });
  });

  return contractClauses;
};

/**
 * Enhanced agent contract clause categories hook
 * Returns the full clause category structure with hierarchical IDs
 */
export const useContractClauseCategories = (
  contractId: string
): ClauseCategory[] => {
  const { comprehensiveClauses } = useContractData(contractId);

  // Convert comprehensive clauses to ClauseCategory format for backward compatibility
  return comprehensiveClauses.map((category) => ({
    id: category.id,
    title: category.title,
    description: category.description,
    icon: category.icon,
    clauses: category.clauses.map((clause) => ({
      id: clause.id,
      hierarchicalId: clause.hierarchicalId,
      title: clause.title,
      description: clause.description,
      value: clause.value,
      originalValue: clause.originalValue,
      category: clause.category,
      subcategory: clause.subcategory,
      source: clause.source,
      isNegotiable: clause.isNegotiable,
      isRequired: clause.isRequired,
      properties: clause.properties,
      validation: clause.validation,
      subClauses: clause.subClauses?.map((subClause) => ({
        id: subClause.id,
        hierarchicalId: subClause.hierarchicalId,
        title: subClause.title,
        description: subClause.description,
        value: subClause.value,
        originalValue: subClause.originalValue,
        category: subClause.category,
        subcategory: subClause.subcategory,
        source: subClause.source,
        isNegotiable: subClause.isNegotiable,
        isRequired: subClause.isRequired,
        properties: subClause.properties,
        validation: subClause.validation,
      })),
    })),
    isCollapsible: category.isCollapsible,
    isExpanded: category.isExpanded,
  }));
};
